/**
 * Tzamun support chat (self-hosted Chatwoot) — SDK wrapper.
 *
 * Three things this file exists to get right:
 *
 * 1. LOAD ONCE. The loader must not run per route. In an SPA that mounts a
 *    second widget over the first, and the SDK gives no warning.
 *
 * 2. NEVER TOUCH $chatwoot BEFORE `chatwoot:ready`. Every method throws until
 *    the iframe is up, and on a slow connection "before" is most of the time.
 *    Callers get a promise instead of a race.
 *
 * 3. NEVER BOOT WITHOUT AN identifierHash. The server mints it (see
 *    services/chatwootIdentity.js) and returns null when the feature is not
 *    configured. Without it, anyone can call setUser with another user's id in
 *    the console and read that person's support history — so `start` refuses
 *    rather than degrading to an unvalidated widget.
 *
 * The widget is loaded ONLY from inside PrivateRoute. Public routes — quotation
 * links, payment pages, /book, /f — are opened by the GARAGE's customers, who
 * are not Tzamun's customers; a support bubble there routes a car owner's
 * "where is my car" into Tzamun IT's inbox.
 */
const BASE_URL = 'https://support.tzamun.sa';

// Public by design: this token identifies the inbox, not the user. Identity is
// carried by the per-user HMAC, which is minted server-side and never here.
//
// The gitleaks annotation is on the line rather than in .gitleaksignore because
// a fingerprint is keyed to a COMMIT hash, and this literal lives permanently in
// the file: squash-merging re-fingerprints it every time, so the ignore entry
// goes stale the moment the PR that added it lands (it did — main went red).
// The secret that would actually matter is CHATWOOT_HMAC_TOKEN, which is
// backend-only and never reaches this bundle.
const WEBSITE_TOKEN = 'bb9AUSqjTeWj3cdRWnR52hdv'; // gitleaks:allow

let scriptInjected = false;
// Set by prepare(), consumed by the first open(). Holds the identity the server
// already handed us so the deferred boot needs no second round trip.
let pendingBoot = null;
let readyPromise = null;
let currentIdentifier = null;
// "The server gave us an identity", NOT "the widget booted". Deliberately
// separate from isReady(): see the note on onStateChange().
let configured = false;
// Read by load() when it builds chatwootSettings, which the SDK consumes once at
// run(). Set before loading so the widget opens on the correct side first time.
let desiredPosition = 'right';

const sdk = () => (typeof window === 'undefined' ? null : window.$chatwoot);

const isArabic = (language) => String(language || 'en').startsWith('ar');

/** Arabic reads right-to-left, so the panel belongs on the left. */
export const positionForLanguage = (language) => (isArabic(language) ? 'left' : 'right');

/**
 * Injects the SDK once and resolves when the widget is usable. Repeat calls
 * return the same promise — mounting twice (StrictMode, a remount, a second
 * caller) must not produce a second widget.
 */
function load() {
  if (readyPromise) return readyPromise;

  readyPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      reject(new Error('chatwoot: no DOM'));
      return;
    }

    // The launcher is ours: MotorMind's own help button. Chatwoot's floating
    // bubble sits bottom-right, which is where the job-card action bar and FABs
    // live on a phone — the app is used one-handed in a workshop.
    // Must be set BEFORE run(); the SDK reads it at boot.
    window.chatwootSettings = {
      ...(window.chatwootSettings || {}),
      position: desiredPosition,
      type: 'standard',
      hideMessageBubble: true,
      // darkMode is deliberately absent: 'auto' follows the OPERATING SYSTEM,
      // which would show a dark widget on a light MotorMind page. Driven from
      // the app's own theme instead, via setColorScheme.
    };

    // Attached before the script is inserted — chatwoot:ready fires once, and a
    // listener added afterwards would simply never hear it.
    window.addEventListener('chatwoot:ready', () => resolve(), { once: true });

    if (scriptInjected) return;
    scriptInjected = true;

    const script = document.createElement('script');
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.defer = true;
    script.async = true;
    script.onload = () => {
      window.chatwootSDK.run({ websiteToken: WEBSITE_TOKEN, baseUrl: BASE_URL });
    };
    script.onerror = () => reject(new Error('chatwoot: sdk failed to load'));
    document.head.appendChild(script);
  });

  return readyPromise;
}

/**
 * Loads the widget and identifies the user. `identity` is the server's
 * `chatwoot` object from /api/auth/me — null when CHATWOOT_HMAC_TOKEN is not
 * set on the server, in which case nothing loads at all.
 */
/**
 * Mark support available WITHOUT downloading the SDK.
 *
 * The widget used to boot on every authenticated page load: the SDK script plus
 * the handshake it makes (contact, conversations, messages) is four-to-five
 * requests and a script download, on every page, for a panel most people never
 * open. Deferring it is free because `configured` and `ready` were already two
 * separate states here — the help drawer gates its support entry on
 * isConfigured(), which this still sets immediately, so the entry appears exactly
 * as before. open() boots on demand.
 */
export function prepare(identity, opts = {}) {
  if (!identity || !identity.identifier || !identity.identifierHash) return false;
  pendingBoot = { identity, opts };
  configured = true;
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('chatwoot:configured'));
  // Position is baked in at run(); set it now so the first painted frame of the
  // eventual boot is on the correct side for the language.
  if (opts.locale) setPosition(positionForLanguage(opts.locale));
  return true;
}

export async function start(identity, { theme, locale, attributes } = {}) {
  if (!identity || !identity.identifier || !identity.identifierHash) return false;

  // Set BEFORE load(), which is the point: if the SDK never boots — Chatwoot
  // down, rate-limited, network blocked — support is still a configured feature
  // that is temporarily unreachable, and the UI must say so rather than vanish.
  configured = true;
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('chatwoot:configured'));

  // BEFORE load(): the SDK reads chatwootSettings.position once, at run(), and
  // bakes the resulting class into every element it creates. Setting it
  // afterwards would render the first frame on the wrong side and then move it.
  if (locale) setPosition(positionForLanguage(locale));

  await load();
  const $chatwoot = sdk();
  if (!$chatwoot) return false;

  // A different person on the same browser must not inherit the session. Logout
  // resets, but a direct user switch (or a stale tab) can reach here without one.
  if (currentIdentifier && currentIdentifier !== identity.identifier) {
    $chatwoot.reset();
  }
  currentIdentifier = identity.identifier;

  $chatwoot.setUser(identity.identifier, {
    ...identity.user,
    identifier_hash: identity.identifierHash,
  });

  const merged = { ...(identity.attributes || {}), ...(attributes || {}) };
  if (Object.keys(merged).length) $chatwoot.setCustomAttributes(merged);

  if (theme) setColorScheme(theme);
  if (locale) setLocale(locale);
  return true;
}

/** theme: 'light' | 'dark'. Never 'auto' — see the note in load(). */
export function setColorScheme(theme) {
  const $chatwoot = sdk();
  if ($chatwoot && (theme === 'light' || theme === 'dark')) $chatwoot.setColorScheme(theme);
}

/**
 * Moves the panel and bubble to one side.
 *
 * The SDK has no setPosition — it bakes `woot-elements--{position}` into the
 * class list at element-creation time and never revisits it. Those elements are
 * in OUR document, though (only the panel body is the cross-origin iframe), so
 * the class is ours to move. window.$chatwoot.position is updated with it
 * because the SDK re-reads that property whenever it creates another element —
 * the bubble, or the holder after a reset — and would otherwise stamp the old
 * side onto the new node.
 */
export function setPosition(side) {
  const position = side === 'left' ? 'left' : 'right';
  desiredPosition = position;
  if (typeof window === 'undefined') return;
  if (window.$chatwoot) window.$chatwoot.position = position;
  if (window.chatwootSettings) window.chatwootSettings.position = position;
  if (typeof document === 'undefined') return;

  document.querySelectorAll('.woot-elements--left, .woot-elements--right').forEach((el) => {
    el.classList.remove('woot-elements--left', 'woot-elements--right');
    el.classList.add(`woot-elements--${position}`);
  });
}

/**
 * 'ar' switches the widget to Arabic and RTL, AND moves it to the left.
 *
 * Deliberately one call rather than two: on this app the side is a function of
 * the language, so exposing them separately would only create a way for a
 * caller to leave an Arabic panel sitting on the right.
 */
export function setLocale(language) {
  setPosition(positionForLanguage(language));
  const $chatwoot = sdk();
  if ($chatwoot) $chatwoot.setLocale(isArabic(language) ? 'ar' : 'en');
}

/**
 * Returns false when the widget is not up, so the caller can say why.
 *
 * Stays SYNCHRONOUS for callers that just want to know whether they could open
 * it. When the boot was deferred by prepare(), this kicks it off and reports
 * true — the widget opens a moment later, once the SDK lands, which is the same
 * experience as clicking while it was still booting under the old eager load.
 */
export function open() {
  const $chatwoot = sdk();
  if ($chatwoot) {
    $chatwoot.toggle('open');
    return true;
  }
  if (pendingBoot) {
    const { identity, opts } = pendingBoot;
    pendingBoot = null;
    start(identity, opts).then((ok) => {
      if (ok) {
        const booted = sdk();
        if (booted) booted.toggle('open');
      }
    });
    return true;
  }
  return false;
}

/** True once the widget has actually booted and can be opened. */
export function isReady() {
  return !!sdk();
}

/** True once the server has handed us an identity, boot succeeded or not. */
export function isConfigured() {
  return configured;
}

/**
 * Calls back when the widget becomes usable, immediately if it already is.
 * Returns an unsubscribe.
 *
 * Readiness has to be SUBSCRIBABLE, not just pollable: React re-renders on state
 * changes, and the SDK finishing its handshake is not one. Reading isReady()
 * during render meant a help drawer opened a moment too early showed no support
 * entry and never grew one — the widget booted, nothing re-rendered, and the
 * link stayed missing until the drawer was closed and opened again. On a slow
 * connection that is the common case, and a missing support button is the one
 * bug a support feature cannot afford.
 */
export function onReady(callback) {
  if (sdk()) {
    callback();
    return () => {};
  }
  const handler = () => callback();
  window.addEventListener('chatwoot:ready', handler, { once: true });
  return () => window.removeEventListener('chatwoot:ready', handler);
}

/**
 * Calls back whenever isConfigured() or isReady() may have changed. Returns an
 * unsubscribe.
 *
 * Gating the help drawer's support entry on isReady() alone conflated two very
 * different states: "this deployment has no support chat" and "support chat is
 * configured but did not come up". The second one silently deleted the button,
 * which reads as a removed feature rather than an outage — 2026-08-09, when
 * Chatwoot's own Rack::Attack throttled /widget (5 loads/hour/IP with no live
 * conversation token) and the entry disappeared mid-test with nothing to say so.
 */
export function onStateChange(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = () => callback();
  window.addEventListener('chatwoot:ready', handler);
  window.addEventListener('chatwoot:configured', handler);
  return () => {
    window.removeEventListener('chatwoot:ready', handler);
    window.removeEventListener('chatwoot:configured', handler);
  };
}

/**
 * Called on logout. Without it the next person to use this browser inherits the
 * previous user's conversation.
 */
export function reset() {
  pendingBoot = null;
  const $chatwoot = sdk();
  if ($chatwoot) $chatwoot.reset();
  currentIdentifier = null;
  configured = false;
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('chatwoot:configured'));
}

// Tests only: the module holds load-once state by design.
export function __resetForTests() {
  scriptInjected = false;
  readyPromise = null;
  currentIdentifier = null;
  desiredPosition = 'right';
  configured = false;
}
